import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileBarChart, Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';

const ANNI = [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2];
const it = (d) => new Date(d).toLocaleDateString('it-IT');

const tabella = (titolo, righe, intestazioni) => righe.length ? `
  <h3 style="margin:16px 0 4px;font-size:13px">${titolo}</h3>
  <table style="width:100%;border-collapse:collapse" border="1" cellpadding="5">
    <thead><tr>${intestazioni.map(h => `<th align="left" style="font-size:11px">${h}</th>`).join('')}</tr></thead>
    <tbody>${righe.map(r => `<tr>${r.map(c => `<td style="font-size:11px">${c}</td>`).join('')}</tr>`).join('')}</tbody>
  </table>` : '';

export default function ReportCadute() {
  const [open, setOpen] = useState(false);
  const [anno, setAnno] = useState(String(new Date().getFullYear()));
  const [trimestre, setTrimestre] = useState(String(Math.floor(new Date().getMonth() / 3) + 1));
  const [loading, setLoading] = useState(false);

  const genera = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get('/cadute/report-trimestrale', { params: { anno, trimestre } });
      const v = r.confronto.variazione_pct;
      const confronto = r.confronto.cadute_precedenti === 0 && r.totali.cadute === 0
        ? 'Nessuna caduta in entrambi i trimestri.'
        : v === null
          ? `Trimestre precedente senza cadute registrate (ora ${r.totali.cadute}).`
          : `${v > 0 ? 'In aumento' : v < 0 ? 'In diminuzione' : 'Stabile'} del ${Math.abs(v)}% rispetto al trimestre precedente `
            + `(${r.confronto.cadute_precedenti} cadute, indice ${r.confronto.indice_precedente}).`;

      openPrintWindow(`Report cadute ${r.trimestre}T ${r.anno}`, `
        <h2>Report trimestrale delle cadute</h2>
        <p><b>Periodo:</b> ${r.trimestre}&deg; trimestre ${r.anno} (dal ${it(r.periodo.da)} al ${it(r.periodo.a)}) &nbsp;&nbsp;
        <b>Ospiti in carico:</b> ${r.ospiti_in_carico}</p>

        <table style="width:100%;border-collapse:collapse" border="1" cellpadding="6">
          <tbody>
            <tr><td><b>Cadute totali</b></td><td align="center">${r.totali.cadute}</td>
                <td><b>Ospiti coinvolti</b></td><td align="center">${r.totali.ospiti_coinvolti}</td></tr>
            <tr><td><b>Cadute con esito grave</b></td><td align="center">${r.totali.gravi}</td>
                <td><b>Cadute nel turno notte</b></td><td align="center">${r.totali.notturne}</td></tr>
            <tr><td><b>Indice per 1000 giornate di degenza</b></td><td align="center">${r.totali.indice_per_1000_giornate}</td>
                <td><b>Trimestre precedente</b></td><td align="center">${r.confronto.indice_precedente}</td></tr>
          </tbody>
        </table>
        <p style="margin-top:8px"><b>Andamento:</b> ${confronto}</p>
        <p style="font-size:10px;color:#64748b">L'indice per 1000 giornate di degenza e calcolato sulle giornate teoriche
        degli ospiti attualmente in carico (${r.ospiti_in_carico}) nel periodo considerato.</p>

        ${tabella('Distribuzione per gravita', r.per_gravita.map(g => [g.label, g.n]), ['Gravita', 'N.'])}
        ${tabella('Distribuzione per luogo', r.per_luogo.map(l => [l.luogo, l.n]), ['Luogo', 'N.'])}
        ${tabella('Distribuzione per turno', r.per_turno.map(t => [t.turno, t.n]), ['Turno', 'N.'])}
        ${tabella('Attivita al momento della caduta', r.per_attivita.map(a => [a.attivita, a.n]), ['Attivita', 'N.'])}
        ${tabella('Ospiti con cadute ripetute', r.recidivi.map(x => [x.ospite_nome, x.stanza, x.cadute]), ['Ospite', 'Camera', 'Cadute'])}
        ${tabella('Elenco degli eventi', r.elenco.map(e => [
          new Date(e.data_ora).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }),
          e.ospite_nome, e.luogo, e.turno, e.gravita, e.dinamica,
        ]), ['Data e ora', 'Ospite', 'Luogo', 'Turno', 'Gravita', 'Dinamica'])}

        <h3 style="margin:16px 0 4px;font-size:13px">Azioni correttive proposte</h3>
        <ol style="font-size:11px;padding-left:18px">${r.azioni_correttive.map(a => `<li style="margin-bottom:3px">${a}</li>`).join('')}</ol>

        <p style="font-size:11px;margin-top:20px">Report generato il ${it(r.generato_il)} da ${r.generato_da}.</p>
        <table style="width:100%;margin-top:30px;font-size:11px"><tr>
          <td>Il Coordinatore<br/><br/>______________________________</td>
          <td>Il Direttore Sanitario<br/><br/>______________________________</td>
        </tr></table>
      `);
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nella generazione del report'); }
    setLoading(false);
  };

  return (
    <>
      <Button variant="outline" className="gap-2" onClick={() => setOpen(true)} data-testid="report-cadute-btn">
        <FileBarChart className="w-4 h-4" />Report trimestrale
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Report trimestrale delle cadute</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Il report include indice cadute per 1000 giornate di degenza, confronto con il trimestre precedente,
              distribuzione per luogo, turno, gravita e attivita, ospiti recidivi, elenco degli eventi,
              azioni correttive e spazio per le firme.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Select value={anno} onValueChange={setAnno}>
                <SelectTrigger data-testid="report-anno"><SelectValue /></SelectTrigger>
                <SelectContent>{ANNI.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={trimestre} onValueChange={setTrimestre}>
                <SelectTrigger data-testid="report-trimestre"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4].map(t => <SelectItem key={t} value={String(t)}>{t}° trimestre</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button onClick={genera} disabled={loading} className="bg-brand hover:bg-brand-dark text-white gap-2" data-testid="genera-report-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                {loading ? 'Generazione...' : 'Genera e stampa'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
